import * as Page from '../../../components/content/page-header';
import * as Button from '../../../components/button/button';
import { Content as LayoutContent } from '../../../components/layout/content';
import { Icons } from '../../../components/icons/icons';
import usePageState from '../../../hooks/use-page-state';
import { Modes } from '../../../types/page-modes';
import { Actions, Content, Dialog, Title } from '../../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import { SortableTable } from '../../../components/table/sortable-table';
import { useNavigate } from 'react-router';
import { Pageable, usePagination } from '../../../hooks/use-pagination';
import { Palette } from './types';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../../hooks/use-mutations';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FileInputLarge } from '../../../components/forms/file';
import { toast } from 'react-toastify';
import { createJSONFile } from '../../../utils/json-file';
import { alert } from '../../../components/toast/toast';
import { usePostFormDataMutation } from '../../../hooks/use-form-mutations';

export const Palettes = (): JSX.Element => {
  const { data, isLoading } = usePagination<Pageable<Palette>>('/palettes', { size: 9999 });
  const [rows, setRows] = useState<Palette[]>([]);
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { register: registerUpload, handleSubmit: handleUploadSubmit, setValue, formState: { errors: uploadErrors } } = useForm();
  const navigate = useNavigate();
  const postMutation = usePostMutation<Palette>({ path: '/palettes' });
  const deleteMutation = useDeleteMutation<Palette>({ cacheKey: '/palettes' });
  const putMutation = usePutMutation({ path: '/palettes/reorder' });
  const uploadFile = usePostFormDataMutation({ path: '/palettes/upload', cacheKey: '/palettes' });
  const downloadMutation = usePostMutation();
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    setRows(data?.content || []);
  }, [data?.content]);

  useEffect(() => {
    if (uploadFile.isSuccess) {
      setPageState(Modes.BROWSE);
      toast('File uploaded successfully', { type: 'success', position: 'top-center' });
    }
  }, [uploadFile.isSuccess]);

  const onSubmit = (data: FieldValues): void => {
    setPageState(Modes.BROWSE);
    postMutation.mutate(data);
    reset();
  };

  const onDeleteConfirm = (): void => {
    setPageState(Modes.BROWSE);
    deleteMutation.mutate({ path: `/palettes/${pageState.item?.id as string}` });
  };

  const onDragEnd = (_: string, data: Palette[]): void => {
    setRows(data);
    putMutation.mutate(data.map(item => item.id));
  };

  const onUploadSubmit = (values: FieldValues): void => {
    uploadFile.mutate(values);
  };

  const importPalette = (): void => {
    setPageState(Modes.UPLOAD);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.click();
      }
    }, 0);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue('file', event.target.files);
  };

  const onExport = async (item: Palette): Promise<void> => {
    try {
      const result = await downloadMutation.mutateAsync({ path: `/palettes/download/${item?.id as string}` });
      createJSONFile(item.name, JSON.stringify(result));
    } catch(exception) {
      console.error('exception', exception);
      alert.error('Failed to export palette');
    }
  };

  return <>
    <LayoutContent>
      <Page.Header>
        <Page.Title>Palettes</Page.Title>
        <Page.SubTitle>
          Symbol Palettes are used to provide custom symbology for items in RAPIDS.
          <div>
            <Button.TertiarySmall onClick={():void => navigate('/settings/palettes/placemarks')}>
              <Icons.Task />
              All Placemarks
            </Button.TertiarySmall>
            <Button.LightBlackSmall onClick={importPalette}>
              <Icons.Import />
              Import
            </Button.LightBlackSmall>
            <Button.PrimarySmall onClick={(): void => setPageState(Modes.CREATE)}>
              <Icons.Add />
              Add Palette
            </Button.PrimarySmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      <SortableTable
        isLoading={isLoading}
        columns={columns}
        rows={rows}
        onDragEnd={onDragEnd}
        onDelete={(item): void => setPageState(Modes.DELETE, item)}
        onUpdate={(item): void => navigate(`/settings/palettes/${item.id as string}`)}
        onExport={onExport}
      />
    </LayoutContent>
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Add Palette</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">Palette Name</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Palette Name.' })} errors={errors} autoFocus />
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              <Icons.Add /> Add
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Palette</Title>
        <Content>Are you sure you want to delete?</Content>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
    {Modes.UPLOAD === pageState.mode && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleUploadSubmit(onUploadSubmit)}>
          <Title>Import Palette</Title>
          <Content>
            <div className="mb-4">
              <Label>Palette File</Label>
              <FileInputLarge { ...registerUpload('file', { required: 'You must provide a value for Palette File.' })} errors={uploadErrors} accept="application/json" ref={inputRef} onChange={onFileChange} />
              <span className="supported-file-format">Supported file format: JSON</span>
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)} disabled={uploadFile.isLoading}>Cancel</Button.Tertiary>
            <Button.Primary type="submit" disabled={uploadFile.isLoading}>Import</Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
  </>;
};

const columns = [
  { accessor: '', Header: 'Sort' },
  { accessor: 'name', Header: 'Name' },
  { accessor: 'count', Header: 'Sections' },
];
